import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Store } from '../types';

interface StorePickerProps {
  stores: Store[];
  current: Store | null;
  onSelect: (store: Store) => void;
  clearLabel?: string;
  onClear?: () => void;
  triggerStyle?: React.CSSProperties;
}

export const StorePicker: React.FC<StorePickerProps> = ({
  stores,
  current,
  onSelect,
  clearLabel,
  onClear,
  triggerStyle,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    if (!q) return stores;
    return stores.filter(
      (s) => s.legibleName.toLowerCase().includes(q) || s.storeName.toLowerCase().includes(q)
    );
  }, [stores, query]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={
          triggerStyle
            ? 'flex items-center gap-2'
            : 'flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm min-w-[240px]'
        }
        style={triggerStyle}
      >
        <span className="truncate text-left flex-1">
          {current ? current.legibleName : clearLabel || 'Select store'}
        </span>
        {triggerStyle ? (
          <KeyboardArrowDownIcon style={{ fontSize: 16, opacity: 0.6 }} />
        ) : (
          <KeyboardArrowDownIcon className="w-4 h-4 text-gray-500" />
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-80 bg-white border border-gray-200 rounded-md shadow-lg z-20">
          <div className="p-2 border-b">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to filter..."
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="max-h-80 overflow-y-auto">
            {clearLabel && onClear ? (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setQuery('');
                  onClear();
                }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                  current ? '' : 'bg-blue-50 text-blue-700'
                }`}
              >
                <div className="truncate font-medium">{clearLabel}</div>
              </button>
            ) : null}
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
            ) : (
              filtered.map((s) => {
                const isCurrent = current?.storeName === s.storeName;
                return (
                  <button
                    key={s.storeName}
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      setQuery('');
                      if (!isCurrent) onSelect(s);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                      isCurrent ? 'bg-blue-50 text-blue-700' : ''
                    }`}
                  >
                    <div className="truncate font-medium">{s.legibleName}</div>
                    <div className="text-xs text-gray-500 truncate">{s.storeName}</div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
